import { createSelector } from 'reselect'

import { EMPTY_ARRAY } from 'utils/emptyConstants'

const root = (state) => state.settings.creditsManagement.self

const selectOverviewData = (state) => root(state).overview.data

export const selectOverview = (state) => selectOverviewData(state)
export const selectOverviewLoader = (state) => root(state).overview.loader
export const selectOverviewError = (state) => root(state).overview.error

export const selectMonthlyUsage = (state) => selectOverviewData(state)?.thisMonthUsage
export const selectHistoricalUsage = (state) => selectOverviewData(state)?.historicalUsage

const selectCreditsUsedThisMonth = (state) => selectOverviewData(state)?.creditsUsedThisMonth

/**
 * Billable credits used this month (PAYG + prepurchase), excluding free/PLG
 * preview credits.
 */
export const selectBillableCreditsUsed = (state) => {
  const usage = selectCreditsUsedThisMonth(state)
  return (usage?.payg ?? 0) + (usage?.prepurchase ?? 0)
}

/**
 * Paid overview totals omit PLG preview credits. `totalUsedCredits` counts
 * every pool, including preview usage from before the tenant purchased.
 */
export const creditsUsedExcludingPlg = (usage) => {
  const total = usage?.totalUsedCredits ?? 0
  const plg = usage?.plg ?? 0
  return Math.max(0, total - plg)
}

export const selectTimeSavedMinutes = (state) => selectOverviewData(state)?.timeSavedMinutes ?? 0

/**
 * CM returns one `timeSavedMinutes` total for every invocation, including PLG
 * preview runs, and does not split it by credit pool. When this month's credits
 * are entirely PLG, none of that time belongs on the paid overview. A mixed
 * month stays on the aggregate — there is no per-pool minute count to subtract.
 */
export const timeSavedMinutesForPaidOverview = (minutes, usage) => {
  const plg = usage?.plg ?? 0
  if (plg > 0 && creditsUsedExcludingPlg(usage) === 0) return 0
  return minutes ?? 0
}

export const selectHistoricalFilters = (state) => root(state).historical.filters
export const selectHistoricalExporting = (state) => root(state).historical.exporting

export const selectPeoplePage = (state) => root(state).people.page
export const selectPeoplePageSize = (state) => root(state).people.pageSize

export const selectAllPeopleRows = (state) =>
  selectOverviewData(state)?.creditUsageByUser ?? EMPTY_ARRAY

export const selectPeopleRows = createSelector(
  selectAllPeopleRows,
  selectPeoplePage,
  selectPeoplePageSize,
  (all, page, pageSize) => {
    const start = (page - 1) * pageSize
    return all.slice(start, start + pageSize)
  }
)

export const selectPeopleTotal = (state) => selectAllPeopleRows(state).length

export const selectPeopleLoader = (state) => root(state).overview.loader

export const selectFirstError = (state) => root(state).overview.error || null

export const selectHistoryFilters = (state) => root(state).history.filters

export const selectHistoryLog = (state) => root(state).history.log.data
export const selectHistoryLogTotal = (state) => root(state).history.log.totalCount
export const selectHistoryLogPageSize = (state) => root(state).history.log.pageSize
export const selectHistoryLogLoader = (state) => root(state).history.log.loader
export const selectHistoryLogError = (state) => root(state).history.log.error

const billingRoot = (state) => root(state).billing

// billing/status lives in the static `agents` slice (see
// containers/Agents/creditSelectors.js) so it is reachable outside /settings.
// Do not re-add a copy here — `billingRoot` has no `status` sub-state.

export const selectInvoices = (state) => billingRoot(state)?.invoices?.data ?? EMPTY_ARRAY
export const selectInvoicesLoader = (state) => billingRoot(state)?.invoices?.loader
export const selectInvoicesError = (state) => billingRoot(state)?.invoices?.error
export const selectInvoiceSort = (state) => {
  const sort = billingRoot(state)?.invoiceSort
  return { key: sort?.sortBy ?? 'date', direction: sort?.sortDir ?? 'desc' }
}

export const selectPricing = (state) => billingRoot(state)?.pricing?.data
export const selectPricingLoader = (state) => billingRoot(state)?.pricing?.loader
export const selectPricingError = (state) => billingRoot(state)?.pricing?.error

export const selectSetupIntent = (state) => billingRoot(state)?.setupIntent?.data
export const selectSetupIntentLoader = (state) => billingRoot(state)?.setupIntent?.loader
export const selectSetupIntentError = (state) => billingRoot(state)?.setupIntent?.error
export const selectSetupIntentUrl = (state) => selectSetupIntent(state)?.url ?? null

export const selectPortalSession = (state) => billingRoot(state)?.portalSession?.data
export const selectPortalSessionLoader = (state) => billingRoot(state)?.portalSession?.loader
export const selectPortalSessionError = (state) => billingRoot(state)?.portalSession?.error
export const selectPortalSessionUrl = (state) => selectPortalSession(state)?.url ?? null

export const selectCheckoutSessionLoader = (state) => billingRoot(state)?.checkoutSession?.loader
export const selectCheckoutSessionError = (state) => billingRoot(state)?.checkoutSession?.error

export const selectLimitUpdateLoader = (state) => billingRoot(state)?.limitUpdate?.loader
export const selectLimitUpdateError = (state) => billingRoot(state)?.limitUpdate?.error

export const selectCancelSubscriptionLoader = (state) =>
  billingRoot(state)?.cancelSubscription?.loader
export const selectCancelSubscriptionError = (state) =>
  billingRoot(state)?.cancelSubscription?.error
